import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    UserCog, UserPlus, RefreshCw, Search, Loader2, Trash2, Edit3,
    ShieldCheck, ShieldOff, Home, KeyRound, Users as UsersIcon,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric' }) : '—';

/**
 * AdminCashiersTab — admin-only UI to create cashiers directly from the admin
 * portal. The cashier is auto-hosted under the hidden "BETohalics Main"
 * house location, and the admin can adopt any orphan customers into Main
 * with a single click so the cashier has an immediate roster.
 */
export const AdminCashiersTab = () => {
    const [home, setHome] = useState(null);          // { id, name, orphan_player_count, main_player_count, cashier_count }
    const [cashiers, setCashiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [adopting, setAdopting] = useState(false);

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        name: '', phone: '', password: '',
        daily_load_limit: 0, can_cashout: true,
    });

    // Edit dialog
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    // Password reset dialog
    const [resetting, setResetting] = useState(null); // { id, name }
    const [newPassword, setNewPassword] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [homeRes, listRes] = await Promise.all([
                axios.get(`${API}/admin/cashiers/home-location`, { withCredentials: true }),
                axios.get(`${API}/admin/cashiers`, { withCredentials: true }),
            ]);
            setHome(homeRes.data);
            setCashiers(listRes.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load cashiers');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const adoptOrphans = async () => {
        if (!home || !home.orphan_player_count) return;
        if (!window.confirm(`Move ${home.orphan_player_count} orphan customers under ${home.name}?`)) return;
        setAdopting(true);
        try {
            const { data } = await axios.post(`${API}/admin/cashiers/adopt-orphans`, {}, { withCredentials: true });
            toast.success(`${data.adopted} customer(s) adopted into ${data.location_name}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Adopt failed');
        } finally {
            setAdopting(false);
        }
    };

    const submitCreate = async (e) => {
        e.preventDefault();
        const name = form.name.trim();
        const phone = form.phone.trim();
        if (!name || !phone || form.password.length < 4) {
            toast.error('Name, phone, and password (4+ chars) required');
            return;
        }
        setCreating(true);
        try {
            const { data } = await axios.post(`${API}/admin/cashiers`, {
                name, phone,
                password: form.password,
                daily_load_limit: Number(form.daily_load_limit) || 0,
                can_cashout: !!form.can_cashout,
            }, { withCredentials: true });
            toast.success(`Cashier "${data.name}" created — they can log in with phone ${data.phone}`);
            setForm({ name: '', phone: '', password: '', daily_load_limit: 0, can_cashout: true });
            setCreateOpen(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Create failed');
        } finally {
            setCreating(false);
        }
    };

    const saveEdit = async () => {
        if (!editing) return;
        setSaving(true);
        try {
            await axios.put(`${API}/admin/cashiers/${editing.id}`, {
                name: editing.name,
                daily_load_limit: Number(editing.daily_load_limit) || 0,
                can_cashout: !!editing.can_cashout,
                status: editing.status,
            }, { withCredentials: true });
            toast.success(`Updated ${editing.name}`);
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Update failed');
        } finally {
            setSaving(false);
        }
    };

    const submitPasswordReset = async () => {
        if (!resetting) return;
        if (newPassword.length < 4) {
            toast.error('Password must be 4+ characters');
            return;
        }
        try {
            await axios.put(`${API}/admin/cashiers/${resetting.id}`, {
                password: newPassword,
            }, { withCredentials: true });
            toast.success(`Password reset for ${resetting.name}`);
            setResetting(null);
            setNewPassword('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reset failed');
        }
    };

    const deleteCashier = async (c) => {
        if (!window.confirm(`Remove cashier "${c.name}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/admin/cashiers/${c.id}`, { withCredentials: true });
            toast.success(`Removed ${c.name}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Delete failed');
        }
    };

    const filtered = cashiers.filter(c => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return (c.name || '').toLowerCase().includes(q)
            || (c.phone || '').includes(q)
            || (c.location_name || '').toLowerCase().includes(q);
    });

    return (
        <div className="space-y-6" data-testid="admin-cashiers-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <UserCog className="w-5 h-5 text-[#FFD700]" />
                    Cashiers
                    <span className="text-xs text-[#A0AAB2] font-normal">({cashiers.length})</span>
                </h3>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}
                        className="border-white/20 text-white h-8 text-[11px]"
                        data-testid="admin-cashiers-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
                    </Button>
                    <Button size="sm" onClick={() => setCreateOpen(true)}
                        className="bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold h-8 text-[11px]"
                        data-testid="admin-cashier-create-btn">
                        <UserPlus className="w-3.5 h-3.5 mr-1" />New Cashier
                    </Button>
                </div>
            </div>

            {/* Home location card */}
            <div className="card-dark p-5" style={{ borderLeft: '3px solid #00E0FF' }}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2">
                            <Home className="w-4 h-4 text-[#00E0FF]" />
                            <h4 className="text-sm font-bold text-white">House Location</h4>
                        </div>
                        <p className="text-xs text-[#A0AAB2] mt-1 max-w-2xl">
                            Your cashiers are hosted under this hidden location so they can serve customers
                            who registered on the public site (i.e. not tied to a storefront).
                        </p>
                        {home && (
                            <div className="flex items-center gap-4 mt-3 flex-wrap">
                                <div className="text-[11px]">
                                    <span className="text-[#A0AAB2]">Name: </span>
                                    <span className="text-white font-bold">{home.name}</span>
                                </div>
                                <div className="text-[11px]">
                                    <UsersIcon className="w-3 h-3 inline mr-1 text-[#39FF14]" />
                                    <span className="text-white font-bold">{home.main_player_count}</span>
                                    <span className="text-[#A0AAB2]"> customers under Main</span>
                                </div>
                                <div className="text-[11px]">
                                    <UserCog className="w-3 h-3 inline mr-1 text-[#FFD700]" />
                                    <span className="text-white font-bold">{home.cashier_count}</span>
                                    <span className="text-[#A0AAB2]"> cashier(s)</span>
                                </div>
                            </div>
                        )}
                    </div>
                    {home && home.orphan_player_count > 0 && (
                        <div className="card-dark p-3 border border-amber-400/30 bg-amber-400/5 min-w-[260px]">
                            <p className="text-[11px] text-amber-300 font-bold mb-1">
                                {home.orphan_player_count} unassigned customer(s)
                            </p>
                            <p className="text-[10px] text-[#A0AAB2] mb-2">
                                These players aren&apos;t tied to any location yet. Adopt them into{' '}
                                <span className="text-white">{home.name}</span> so your cashiers can service them.
                            </p>
                            <Button size="sm" onClick={adoptOrphans} disabled={adopting}
                                className="bg-amber-500 hover:bg-amber-600 text-black font-bold h-7 text-[11px] w-full"
                                data-testid="admin-cashier-adopt-orphans">
                                {adopting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Adopt all into {home.name}</>}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Cashiers list */}
            <div className="card-dark p-5">
                <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0AAB2]" />
                    <Input value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search by name, phone or location…"
                        className="pl-10 bg-[#121212] border-white/10 text-white h-9"
                        data-testid="admin-cashier-search" />
                </div>
                {loading ? (
                    <div className="text-center py-10 text-[#A0AAB2]">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-10 text-[#A0AAB2]">
                        {search ? 'No cashiers match that search.' : 'No cashiers yet. Click "New Cashier" to add one.'}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10">
                                    <TableHead className="text-[#A0AAB2]">Name</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Phone</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Location</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Daily Limit</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-center">Cashout</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Today Loaded</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Today Cashed</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Status</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((c, i) => (
                                    <TableRow key={c.id} className="border-white/5" data-testid={`admin-cashier-row-${i}`}>
                                        <TableCell className="text-white font-bold">{c.name}</TableCell>
                                        <TableCell className="text-[#A0AAB2] font-mono text-sm">{c.phone || '—'}</TableCell>
                                        <TableCell className="text-[#A0AAB2] text-sm">
                                            {c.location_name}
                                            {c.is_house && <span className="ml-1 text-[9px] text-[#00E0FF] font-bold">[HOUSE]</span>}
                                        </TableCell>
                                        <TableCell className="text-right text-white text-sm">
                                            {c.daily_load_limit > 0 ? fmtMoney(c.daily_load_limit) : <span className="text-[#A0AAB2]">∞</span>}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {c.can_cashout
                                                ? <ShieldCheck className="w-4 h-4 text-[#39FF14] inline" />
                                                : <ShieldOff className="w-4 h-4 text-[#FF3B30] inline" />}
                                        </TableCell>
                                        <TableCell className="text-right text-[#39FF14] text-sm font-mono">{fmtMoney(c.loaded_today)}</TableCell>
                                        <TableCell className="text-right text-[#FF3B30] text-sm font-mono">{fmtMoney(c.cashed_today)}</TableCell>
                                        <TableCell>
                                            <span className={`text-xs font-bold ${c.status === 'active' ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                {c.status}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right space-x-1">
                                            <Button size="sm" variant="outline"
                                                onClick={() => setEditing({ ...c })}
                                                className="border-white/20 text-white h-7 w-7 p-0"
                                                title="Edit"
                                                data-testid={`admin-cashier-edit-${i}`}>
                                                <Edit3 className="w-3 h-3" />
                                            </Button>
                                            <Button size="sm" variant="outline"
                                                onClick={() => { setResetting({ id: c.id, name: c.name }); setNewPassword(''); }}
                                                className="border-[#FFD700]/30 text-[#FFD700] h-7 w-7 p-0"
                                                title="Reset password"
                                                data-testid={`admin-cashier-reset-pw-${i}`}>
                                                <KeyRound className="w-3 h-3" />
                                            </Button>
                                            <Button size="sm" variant="outline"
                                                onClick={() => deleteCashier(c)}
                                                className="border-[#FF3B30]/30 text-[#FF3B30] h-7 w-7 p-0"
                                                title="Remove"
                                                data-testid={`admin-cashier-delete-${i}`}>
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
                <p className="text-[10px] text-[#666] mt-3">
                    Cashiers log in at the standard login screen with their phone number + password.
                    They land on the Cashier Dashboard with only the customers from their location visible.
                </p>
            </div>

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-md" data-testid="admin-cashier-create-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-[#FFD700]" /> New Cashier
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            They&apos;ll log in with this phone + password and immediately see customers under{' '}
                            <span className="text-white font-bold">{home?.name || 'House'}</span>.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitCreate} className="space-y-3">
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2]">Cashier name</Label>
                            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. Winnie"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white h-9"
                                data-testid="admin-cashier-form-name" />
                        </div>
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2]">Phone (login id)</Label>
                            <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                                placeholder="2563485023"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white h-9"
                                data-testid="admin-cashier-form-phone" />
                        </div>
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2]">Password</Label>
                            <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white h-9"
                                data-testid="admin-cashier-form-password" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[11px] text-[#A0AAB2]">Daily load limit ($)</Label>
                                <Input type="number" min="0" step="50" value={form.daily_load_limit}
                                    onChange={e => setForm(f => ({ ...f, daily_load_limit: e.target.value }))}
                                    placeholder="0 = unlimited"
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white h-9"
                                    data-testid="admin-cashier-form-limit" />
                            </div>
                            <div className="flex flex-col">
                                <Label className="text-[11px] text-[#A0AAB2]">Allow cashouts?</Label>
                                <div className="flex items-center gap-2 h-9 mt-1">
                                    <Switch checked={!!form.can_cashout}
                                        onCheckedChange={(v) => setForm(f => ({ ...f, can_cashout: v }))}
                                        data-testid="admin-cashier-form-cashout" />
                                    <span className="text-xs text-white">{form.can_cashout ? 'Yes' : 'No'}</span>
                                </div>
                            </div>
                        </div>
                        <DialogFooter className="pt-2">
                            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}
                                className="border-white/10 text-white">Cancel</Button>
                            <Button type="submit" disabled={creating}
                                className="bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold"
                                data-testid="admin-cashier-form-submit">
                                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create cashier'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-md" data-testid="admin-cashier-edit-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white">Edit Cashier</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <div>
                                <Label className="text-[11px] text-[#A0AAB2]">Name</Label>
                                <Input value={editing.name || ''}
                                    onChange={e => setEditing(p => ({ ...p, name: e.target.value }))}
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white h-9" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-[11px] text-[#A0AAB2]">Daily limit ($)</Label>
                                    <Input type="number" min="0" step="50" value={editing.daily_load_limit}
                                        onChange={e => setEditing(p => ({ ...p, daily_load_limit: e.target.value }))}
                                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white h-9" />
                                </div>
                                <div className="flex flex-col">
                                    <Label className="text-[11px] text-[#A0AAB2]">Cashouts</Label>
                                    <div className="flex items-center gap-2 h-9 mt-1">
                                        <Switch checked={!!editing.can_cashout}
                                            onCheckedChange={(v) => setEditing(p => ({ ...p, can_cashout: v }))} />
                                        <span className="text-xs text-white">{editing.can_cashout ? 'Allowed' : 'Blocked'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch checked={editing.status === 'active'}
                                    onCheckedChange={(v) => setEditing(p => ({ ...p, status: v ? 'active' : 'suspended' }))} />
                                <span className="text-xs text-white">
                                    {editing.status === 'active' ? 'Active (can log in)' : 'Suspended (cannot log in)'}
                                </span>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)} className="border-white/10 text-white">Cancel</Button>
                        <Button onClick={saveEdit} disabled={saving}
                            className="bg-[#39FF14] text-black font-bold"
                            data-testid="admin-cashier-edit-save">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Password reset dialog */}
            <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-[#FFD700]" /> Reset password
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Set a new password for <span className="text-white font-bold">{resetting?.name}</span>.
                        </DialogDescription>
                    </DialogHeader>
                    <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                        placeholder="New password (4+ chars)"
                        className="bg-[#0a0a0a] border-white/10 text-white h-9"
                        data-testid="admin-cashier-reset-pw-input" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResetting(null)} className="border-white/10 text-white">Cancel</Button>
                        <Button onClick={submitPasswordReset}
                            className="bg-[#FFD700] text-black font-bold"
                            data-testid="admin-cashier-reset-pw-submit">
                            Save new password
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
